import { isValidCssSelector } from 'models'

/**
 * Reads the page that hosts the widget, the way its user sees it (spec
 * `page-tools.md`): visible elements only, each meaningful one with a role, an
 * accessible name, its state and a `ref` the action tools resolve.
 */

/** `autocomplete` tokens of fields never read nor touched, whatever the level. */
const SENSITIVE_AUTOCOMPLETE =
    /^(cc-.*|one-time-code|current-password|new-password)$/

const SKIPPED_TAGS = new Set([
    'SCRIPT',
    'STYLE',
    'NOSCRIPT',
    'TEMPLATE',
    'SVG',
    'CANVAS',
    'IFRAME',
    'OBJECT',
    'EMBED',
    'VIDEO',
    'AUDIO',
])

/** The widget and its page control bar are not part of the page the agent works on. */
/** The widget, and the stop it puts inside a modal dialog of the page (`usePageModalStop`). */
export const STOP_IN_MODAL_TAG = 'agent-studio-stop'
const WIDGET_TAGS = new Set([
    'EMBEDDED-CHAT-WIDGET',
    'FLOATING-CHAT-WIDGET',
    STOP_IN_MODAL_TAG.toUpperCase(),
])

const INPUT_ROLES: Record<string, string> = {
    button: 'button',
    submit: 'button',
    reset: 'button',
    image: 'button',
    checkbox: 'checkbox',
    radio: 'radio',
    range: 'slider',
    number: 'spinbutton',
    search: 'searchbox',
}

/**
 * Roles that hold the controls the agent acts on: one line, named only by what
 * they declare, then their content. Read as a single line, a menu would be a
 * control named after all its items, and the items would have no ref. Their
 * line is also the scope page_snapshot reads a region by.
 */
const CONTAINER_ROLES = new Set([
    'menu',
    'menubar',
    'listbox',
    'tablist',
    'tabpanel',
    'tree',
    'treegrid',
    'grid',
    'row',
    'radiogroup',
    'toolbar',
    'dialog',
    'alertdialog',
    'navigation',
    'region',
    'main',
    'form',
    'search',
    'banner',
    'contentinfo',
    'complementary',
])

/** Roles of structure and plain content: read as text, with no line of their own. */
const CONTENT_ROLES = new Set([
    'presentation',
    'none',
    'generic',
    'group',
    'list',
    'listitem',
    'table',
    'rowgroup',
    'cell',
    'gridcell',
    'columnheader',
    'rowheader',
    'paragraph',
    'article',
    'figure',
    'document',
    'application',
    'feed',
    'log',
    'note',
    'status',
    'alert',
    'separator',
])

/** A walk stops past either budget, as it does past its character limit. */
const MAX_NODES = 20_000
const MAX_WALK_MS = 250

/** Stable ids for elements across snapshots, without holding removed elements in memory. */
export class PageRefs {
    private next = 1
    private readonly byElement = new WeakMap<Element, number>()
    private readonly byRef = new Map<number, WeakRef<Element>>()

    refOf(element: Element): number {
        const known = this.byElement.get(element)
        if (known !== undefined) {
            return known
        }
        const ref = this.next++
        this.byElement.set(element, ref)
        this.byRef.set(ref, new WeakRef(element))
        return ref
    }

    /** The element behind a ref, or undefined when it left the page. */
    resolve(ref: number): Element | undefined {
        const element = this.byRef.get(ref)?.deref()
        if (!element?.isConnected || element.ownerDocument !== document) {
            this.byRef.delete(ref)
            return undefined
        }
        return element
    }
}

export type PageRules = { excludeSelectors: readonly string[] }

/** False when a selector does not parse: the caller then turns page access off. */
export const selectorsAreValid = (rules: PageRules): boolean =>
    rules.excludeSelectors.every(isValidCssSelector)

/** The element and its ancestors, crossing shadow roots up to their hosts. */
export function* composedAncestors(element: Element): Generator<Element> {
    let node: Node | null = element
    while (node) {
        if (node instanceof Element) {
            yield node
        }
        const parent: Node | null = node.parentNode
        node = parent instanceof ShadowRoot ? parent.host : parent
    }
}

const isSensitiveField = (element: Element): boolean =>
    element instanceof HTMLInputElement &&
    (element.type === 'password' ||
        element.type === 'hidden' ||
        element.autocomplete
            .toLowerCase()
            .split(/\s+/)
            .some((token) => SENSITIVE_AUTOCOMPLETE.test(token)))

/** The rules an element matches by itself, labels aside. */
const matchesExclusion = (element: Element, rules: PageRules): boolean =>
    WIDGET_TAGS.has(element.tagName) ||
    element.hasAttribute('data-agent-ignore') ||
    isSensitiveField(element) ||
    rules.excludeSelectors.some((selector) => element.matches(selector))

/**
 * The field a label names. `control` is null for a field that is not
 * labelable, such as `input[type=hidden]`, which `for` still points to.
 */
const labelled = (label: HTMLLabelElement): Element | null =>
    label.control ??
    (label.htmlFor
        ? (label.getRootNode() as Document | ShadowRoot).getElementById(
              label.htmlFor,
          )
        : null)

/**
 * The exclusion rules of one element, without its ancestors. A label goes
 * with its control: "Password" or "Card number" next to nothing still tells
 * the model the field is there. The control is checked without the label
 * rule, so a field nested in its own label does not loop.
 */
const excludesItself = (element: Element, rules: PageRules): boolean => {
    if (matchesExclusion(element, rules)) {
        return true
    }
    const control =
        element instanceof HTMLLabelElement ? labelled(element) : null
    if (!control) {
        return false
    }
    for (const ancestor of composedAncestors(control)) {
        if (matchesExclusion(ancestor, rules)) {
            return true
        }
    }
    return false
}

/**
 * True for an element the agent must neither read nor touch, itself or
 * through any ancestor, across shadow roots. A walk that skips excluded
 * subtrees only needs `excludesItself` on the way down.
 */
export function isExcluded(element: Element, rules: PageRules): boolean {
    for (const ancestor of composedAncestors(element)) {
        if (excludesItself(ancestor, rules)) {
            return true
        }
    }
    return false
}

const shows = (element: Element) =>
    element.checkVisibility({ checkOpacity: true, checkVisibilityCSS: true })

/**
 * Hidden for the user; `display: contents` has no box but shows its
 * children. An inert subtree counts as hidden for the snapshot (it takes no
 * click nor focus: not a page the agent can use), not with `inert: false`:
 * a modal dialog makes the rest of the page inert without hiding it, the
 * widget's own card included (`formSummary`, the outcome of an action).
 */
export const isHidden = (
    element: Element,
    { inert = true }: { inert?: boolean } = {},
): boolean => {
    if (inert && (element as HTMLElement).inert) {
        return true
    }
    if (typeof element.checkVisibility !== 'function') {
        return false
    }
    if (shows(element)) {
        return false
    }
    // A styled checkbox or radio hides its own input and shows its label:
    // the user picks it through the label, and so does the agent
    if (
        element instanceof HTMLInputElement &&
        (element.type === 'checkbox' || element.type === 'radio') &&
        [...(element.labels ?? [])].some(shows)
    ) {
        return false
    }
    return getComputedStyle(element).display !== 'contents'
}

const clean = (text: string | null | undefined, max = 120): string => {
    const collapsed = (text ?? '').replace(/\s+/g, ' ').trim()
    return collapsed.length > max
        ? `${collapsed.slice(0, max - 1)}…`
        : collapsed
}

/** The children a reader meets: the shadow tree, or what a slot shows. */
const childrenOf = (node: Node): Node[] => {
    if (node instanceof HTMLSlotElement) {
        const assigned = node.assignedNodes({ flatten: true })
        return assigned.length > 0 ? assigned : [...node.childNodes]
    }
    if (node instanceof Element && node.shadowRoot) {
        return [...node.shadowRoot.childNodes]
    }
    return [...node.childNodes]
}

/** The text of an element through the same filter as the snapshot. */
export function filteredText(
    root: Element,
    rules: PageRules,
    max: number,
): string {
    const parts: string[] = []
    let length = 0
    const visit = (node: Node) => {
        if (length > max) {
            return
        }
        if (node.nodeType === Node.TEXT_NODE) {
            const text = node.textContent ?? ''
            parts.push(text)
            length += text.length
            return
        }
        if (!(node instanceof Element)) {
            return
        }
        if (SKIPPED_TAGS.has(node.tagName) || excludesItself(node, rules)) {
            return
        }
        if (isHidden(node)) {
            return
        }
        for (const child of childrenOf(node)) {
            visit(child)
        }
    }
    if (isExcluded(root, rules)) {
        return ''
    }
    visit(root)
    return clean(parts.join(' '), max)
}

/**
 * A cell of a grid the user picks from, as a calendar's day: focusable or
 * selectable, with no control of its own inside (react-datepicker's days on
 * demoqa, `tabindex` and `aria-selected`; MUI's are buttons with the role).
 * Read as text, the day could not be clicked, and two models gave up on the
 * date (2026-10-28). A data grid's plain cell stays text.
 */
const choosableCell = (element: Element, role: string) =>
    role === 'gridcell' &&
    (element.hasAttribute('aria-selected') ||
        element.hasAttribute('tabindex') ||
        element instanceof HTMLButtonElement) &&
    !element.querySelector(
        'a[href], button, input, select, textarea, [role], [tabindex]',
    )

/** The role a screen reader would announce, or undefined for a plain container. */
export function roleOf(element: Element): string | undefined {
    const explicit = element.getAttribute('role')
    if (explicit) {
        return explicit.split(' ')[0]
    }
    const tag = element.tagName
    if (/^H[1-6]$/.test(tag)) {
        return 'heading'
    }
    switch (tag) {
        case 'A':
            return element.hasAttribute('href') ? 'link' : undefined
        case 'BUTTON':
        case 'SUMMARY':
            return 'button'
        case 'TEXTAREA':
            return 'textbox'
        case 'SELECT':
            return 'combobox'
        case 'IMG':
            return element.getAttribute('alt') ? 'img' : undefined
        case 'INPUT':
            return INPUT_ROLES[(element as HTMLInputElement).type] ?? 'textbox'
        default:
            return (element as HTMLElement).isContentEditable &&
                !(element.parentElement as HTMLElement | null)
                    ?.isContentEditable
                ? 'textbox'
                : undefined
    }
}

/** The name an element declares with `aria-label` or `aria-labelledby`, never from an excluded element. */
function declaredName(element: Element, rules: PageRules): string {
    const label = element.getAttribute('aria-label')
    if (label) {
        return clean(label)
    }
    const labelledBy = element.getAttribute('aria-labelledby')
    if (!labelledBy) {
        return ''
    }
    return clean(
        labelledBy
            .split(' ')
            .map((id) => element.ownerDocument.getElementById(id))
            .filter((target): target is HTMLElement => !!target)
            .filter((target) => !isExcluded(target, rules))
            .map((target) => filteredText(target, rules, 120))
            .join(' '),
    )
}

/** How far up a field without a name looks for the label written beside it. */
const NEARBY_LABEL_DEPTH = 10
/** How far above a field, in pixels, the label written over it may end. */
const LABEL_ABOVE_MAX = 48

/**
 * True when `label` sits where the label of `field` is read: on the field's
 * row, on either side, or right above it. Where nothing is laid out, the
 * structure alone decides.
 */
const besideField = (label: Element, field: Element): boolean => {
    const box = field.getBoundingClientRect()
    if (!box.width && !box.height) {
        return true
    }
    const near = label.getBoundingClientRect()
    const sameRow =
        near.bottom > box.top &&
        near.top < box.bottom &&
        (near.right <= box.left + 1 || near.left >= box.right - 1)
    const above =
        near.bottom <= box.top + 1 &&
        box.top - near.bottom <= LABEL_ABOVE_MAX &&
        near.left < box.right &&
        near.right > box.left
    return sameRow || above
}

/**
 * The label a page writes beside a field without tying it to the field: a
 * `<label>` with no `for` and no control inside, in another column of the
 * same row (seen on demoqa, spec `page-tools-improvements.md`, M10). The
 * only one before the field in its nearest ancestor that holds any, on its
 * row or right above it, never past its form or dialog, never the whole
 * page. A guess, and it reaches the confirmation cards: two fields of one row
 * share it.
 */
const nearbyLabel = (field: Element, rules: PageRules): string => {
    // Most pages tie every label: one query spares them the walk
    const scope =
        field.closest('form, fieldset, dialog, [role=dialog]') ??
        field.ownerDocument.body
    if (!scope.querySelector('label:not([for]), label[for=""]')) {
        return ''
    }
    let ancestor = field.parentElement
    for (
        let depth = 0;
        ancestor &&
        ancestor !== field.ownerDocument.body &&
        depth < NEARBY_LABEL_DEPTH;
        depth++, ancestor = ancestor.parentElement
    ) {
        const [first, ...others] = [
            ...ancestor.querySelectorAll('label'),
        ].filter(
            (label) =>
                !label.htmlFor &&
                !label.control &&
                !!(
                    label.compareDocumentPosition(field) &
                    Node.DOCUMENT_POSITION_FOLLOWING
                ) &&
                !isExcluded(label, rules) &&
                !isHidden(label) &&
                besideField(label, field),
        )
        if (first) {
            return others.length === 0 ? filteredText(first, rules, 120) : ''
        }
        if (ancestor.matches('form, fieldset, dialog, [role=dialog]')) {
            return ''
        }
    }
    return ''
}

/** The accessible name, in the order the accessibility tree computes it, never from an excluded element. */
export function nameOf(element: Element, rules: PageRules): string {
    const declared = declaredName(element, rules)
    if (declared) {
        return declared
    }
    // Its text is the names of the controls it holds, each on its own line
    if (CONTAINER_ROLES.has(roleOf(element) ?? '')) {
        return ''
    }
    const labels = (element as HTMLInputElement).labels
    if (labels?.length) {
        return clean(
            [...labels].map((l) => filteredText(l, rules, 120)).join(' '),
        )
    }
    if (element instanceof HTMLImageElement) {
        return clean(element.alt)
    }
    if (
        element instanceof HTMLInputElement &&
        ['submit', 'button', 'reset'].includes(element.type)
    ) {
        return clean(element.value || element.type)
    }
    const field =
        element instanceof HTMLInputElement ||
        element instanceof HTMLTextAreaElement ||
        element instanceof HTMLSelectElement
    const text = field ? '' : filteredText(element, rules, 120)
    return (
        text ||
        clean(element.getAttribute('placeholder')) ||
        clean(element.getAttribute('title')) ||
        (field ? nearbyLabel(element, rules) : '')
    )
}

/** Roles whose active item a library may mark with a class alone. */
const CURRENT_CLASS_ROLES = new Set([
    'tab',
    'menuitem',
    'link',
    'button',
    'option',
])
const CURRENT_CLASSES = new Set([
    'current',
    'active',
    'is-active',
    'selected',
    'is-selected',
    'router-link-exact-active',
])

const FIELD_SELECTOR =
    'input:not([type=hidden]):not([type=button]):not([type=submit]):not([type=reset]), textarea, select, [contenteditable=""], [contenteditable=true]'

/**
 * A dialog that asks rather than edits: no field, or only what a confirmation
 * asks for (an "I understand" box, one line where to type the name of what is
 * deleted). A text area, a rich editor, a select or a second line of text make
 * it a form, which its own save confirms where it submits.
 */
const onlyAsks = (dialog: Element): boolean => {
    let lines = 0
    for (const field of dialog.querySelectorAll(FIELD_SELECTOR)) {
        if (
            field instanceof HTMLInputElement &&
            (field.type === 'checkbox' || field.type === 'radio')
        ) {
            continue
        }
        if (!(field instanceof HTMLInputElement) || ++lines > 1) {
            return false
        }
    }
    return true
}

const isModal = (dialog: Element): boolean => {
    if (
        dialog.getAttribute('role') === 'alertdialog' ||
        dialog.getAttribute('aria-modal') === 'true'
    ) {
        return true
    }
    try {
        return dialog.matches(':modal')
    } catch {
        // A browser without `:modal`: an open native dialog is the best guess
        return dialog instanceof HTMLDialogElement && dialog.open
    }
}

/** Whether `node` is inside `container`, across the shadow roots between them. */
const holds = (container: Element, node: Node): boolean => {
    for (let current: Node | null = node; current;) {
        if (container.contains(current)) {
            return true
        }
        const root = current.getRootNode()
        current = root instanceof ShadowRoot ? root.host : null
    }
    return false
}

/** The open shadow roots of the page, nested ones included; never the widget's. */
export function openShadowRoots(doc: Document): ShadowRoot[] {
    const found: ShadowRoot[] = []
    const visit = (scope: Document | ShadowRoot) => {
        for (const element of scope.querySelectorAll('*')) {
            if (element.shadowRoot && !WIDGET_TAGS.has(element.tagName)) {
                found.push(element.shadowRoot)
                visit(element.shadowRoot)
            }
        }
    }
    visit(doc)
    return found
}

/**
 * The modal `<dialog>`s open on the page, in its open shadow roots too.
 * Native modals only: an `aria-modal` dialog of a library traps the focus
 * but leaves the page reachable, while the browser makes everything outside
 * a native one inert, a popover it opens in the top layer included unless
 * it sits inside the dialog (checked in Chrome, 2026-10-26).
 */
const modalDialogs = (doc: Document): HTMLDialogElement[] => {
    try {
        return [doc, ...openShadowRoots(doc)].flatMap((scope) => [
            ...scope.querySelectorAll<HTMLDialogElement>('dialog:modal'),
        ])
    } catch {
        // A browser without `:modal`
        return []
    }
}

/**
 * The modal dialog the user is in, when the page has one open: with more
 * than one, the dialog that takes the point at its own centre (the backdrop
 * of the top one covers those below), else the innermost, since a modal
 * opened from inside another is the one on top.
 */
export function openModal(
    doc: Document = document,
): HTMLDialogElement | undefined {
    const modals = modalDialogs(doc)
    if (modals.length < 2) {
        return modals[0]
    }
    const onTop = modals.filter((dialog) => {
        const box = dialog.getBoundingClientRect()
        const root = dialog.getRootNode() as Document | ShadowRoot
        const hit = root.elementFromPoint?.(
            box.left + box.width / 2,
            box.top + box.height / 2,
        )
        return !!hit && holds(dialog, hit)
    })
    if (onTop.length === 1) {
        return onTop[0]
    }
    const innermost = modals.filter(
        (dialog) =>
            !modals.some((other) => other !== dialog && holds(dialog, other)),
    )
    return innermost[innermost.length - 1]
}

/**
 * The open modal dialog of the page that keeps `element` out of the user's
 * reach: only the one on top takes clicks, a dialog under it is inert as
 * well. A click from a script gets past the inert page where the user
 * cannot: seen on the bench, a tab behind an editor dialog switched its
 * panel and left the dialog open and hidden over the whole page.
 */
export function blockingDialog(
    element: Element,
): HTMLDialogElement | undefined {
    const top = openModal(element.ownerDocument)
    return top && !holds(top, element) ? top : undefined
}

/**
 * The question of the site's own confirmation dialog when `element` answers
 * it: one of its buttons, or with `anyElement` any element in it (a key goes
 * to the dialog's own handler as well). An `alertdialog`, or a modal dialog
 * that only asks ("Delete the agent? This cannot be undone."). The site asks
 * it of the user, so the user answers it, with the question in view, whatever
 * the confirmation settings. Its text without the buttons, never from an
 * excluded element.
 */
export function confirmationQuestion(
    element: Element,
    rules: PageRules,
    { anyElement = false }: { anyElement?: boolean } = {},
): string | undefined {
    const button = anyElement
        ? element
        : element.closest(
              'button, [role=button], input[type=button], input[type=submit]',
          )
    const dialog = button?.closest('dialog, [role=dialog], [role=alertdialog]')
    if (
        !dialog ||
        !isModal(dialog) ||
        (dialog.getAttribute('role') !== 'alertdialog' && !onlyAsks(dialog))
    ) {
        return undefined
    }
    const parts: string[] = []
    const walker = element.ownerDocument.createTreeWalker(
        dialog,
        NodeFilter.SHOW_TEXT,
    )
    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
        const parent = node.parentElement
        if (
            parent &&
            !parent.closest('button, [role=button], a, script, style') &&
            !isExcluded(parent, rules)
        ) {
            parts.push(node.textContent ?? '')
        }
    }
    return clean(parts.join(' '), 240) || undefined
}

const stateOf = (element: Element): string[] => {
    const states: string[] = []
    if ((element as HTMLInputElement).disabled) {
        states.push('disabled')
    }
    if ((element as HTMLInputElement).required) {
        states.push('required')
    }
    // Shown, not typed in: a site's editor often opens from a button beside it
    if (
        (element as HTMLInputElement).readOnly ||
        element.getAttribute('aria-readonly') === 'true'
    ) {
        states.push('readonly')
    }
    if (
        element instanceof HTMLInputElement &&
        (element.type === 'checkbox' || element.type === 'radio')
    ) {
        states.push(element.checked ? 'checked' : 'unchecked')
    }
    const expanded = element.getAttribute('aria-expanded')
    if (expanded) {
        states.push(expanded === 'true' ? 'expanded' : 'collapsed')
    }
    const checked = element.getAttribute('aria-checked')
    if (checked && !(element instanceof HTMLInputElement)) {
        states.push(
            checked === 'true'
                ? 'checked'
                : checked === 'mixed'
                  ? 'mixed'
                  : 'unchecked',
        )
    }
    const pressed = element.getAttribute('aria-pressed')
    if (pressed) {
        states.push(pressed === 'false' ? 'not pressed' : 'pressed')
    }
    if (element.getAttribute('aria-selected') === 'true') {
        states.push('selected')
    }
    const current = element.getAttribute('aria-current')
    if (
        (current && current !== 'false') ||
        // The tab or item a library marks as the active one with a class
        // only: without it, the agent cannot tell which version of a field
        // the page shows (the language of a text, a tab of settings)
        (!current &&
            CURRENT_CLASS_ROLES.has(roleOf(element) ?? '') &&
            [...element.classList].some((name) => CURRENT_CLASSES.has(name)))
    ) {
        states.push('current')
    }
    return states
}

/** The options of a select the agent may see and pick: not the excluded ones. */
export const allowedOptions = (
    select: HTMLSelectElement,
    rules: PageRules,
): HTMLOptionElement[] =>
    [...select.options].filter((option) => !isExcluded(option, rules))

/**
 * What a list box of the page's own (a combobox built with a div, not a
 * select nor an input) shows as chosen: its text without its name, or the
 * options selected in its list when the list is inside it. Empty while it
 * shows its placeholder, which it renders as plain text: the options of the
 * list it controls then carry none selected.
 */
const comboboxChoice = (
    element: Element,
    rules: PageRules,
    max: number,
    {
        byText = false,
    }: {
        /** The text it shows only: steady while its list mounts and unmounts. */
        byText?: boolean
    } = {},
): string => {
    const listId = (
        element.getAttribute('aria-controls') ??
        element.getAttribute('aria-owns') ??
        ''
    ).split(' ')[0]
    const list =
        listId && !byText
            ? (element.getRootNode() as Document | ShadowRoot).getElementById(
                  listId,
              )
            : null
    const options = list ? [...list.querySelectorAll('[role=option]')] : []
    const selected = options.filter(
        (option) =>
            option.getAttribute('aria-selected') === 'true' &&
            !isExcluded(option, rules),
    )
    // A long list that mounts a window of its options may leave the chosen
    // one out: their count says so, and the text it shows is the choice
    const partial = options.some(
        (option) =>
            Number(option.getAttribute('aria-setsize')) > options.length,
    )
    if (options.length > 0 && selected.length === 0 && !partial) {
        return ''
    }
    if (list && element.contains(list) && selected.length > 0) {
        return clean(
            selected.map((option) => nameOf(option, rules)).join(', '),
            max,
        )
    }
    const text = filteredText(element, rules, max + 40)
    // Without a label its name is that same text: the text is the choice
    const name = nameOf(element, rules)
    return clean(
        text !== name && text.startsWith(name) ? text.slice(name.length) : text,
        max,
    )
}

/**
 * The list of options a combobox shows open: the one its field, or the
 * combobox around the field, names with `aria-controls` or `aria-owns` while
 * `aria-expanded` is true. On many sites typing opens it, and the text typed
 * is not a choice until an option is picked (spec
 * `page-tools-improvements.md`, M10).
 */
export const openListOf = (
    element: Element,
    rules: PageRules,
): Element | null => {
    const box = element.closest('[role=combobox][aria-expanded="true"]')
    if (!box) {
        return null
    }
    for (const owner of new Set([element, box])) {
        const listId = (
            owner.getAttribute('aria-controls') ??
            owner.getAttribute('aria-owns') ??
            ''
        ).split(' ')[0]
        const list = listId
            ? (owner.getRootNode() as Document | ShadowRoot).getElementById(
                  listId,
              )
            : null
        // A popup a combobox may open, by its role or its options: an
        // element the attributes merely point at is not a list
        if (
            list &&
            (list.matches(
                '[role=listbox], [role=tree], [role=grid], [role=dialog]',
            ) ||
                !!list.querySelector('[role=option]')) &&
            !isHidden(list, { inert: false }) &&
            !isExcluded(list, rules)
        ) {
            return list
        }
    }
    return null
}

/**
 * The text a text box the page built itself shows (an editor, or a box that
 * shows a text edited elsewhere), without its own name before it, and empty
 * while it shows only its hint (`aria-placeholder`).
 */
const editorText = (
    element: Element,
    rules: PageRules,
    max: number,
): string => {
    const name = nameOf(element, rules)
    const text = filteredText(element, rules, max + 40)
    const shown = clean(
        name && text.startsWith(name) ? text.slice(name.length) : text,
        max,
    )
    return shown === clean(element.getAttribute('aria-placeholder'), max)
        ? ''
        : shown
}

const valueOf = (element: Element, rules: PageRules): string | undefined => {
    if (element instanceof HTMLSelectElement) {
        const selected = element.selectedOptions[0]
        return selected && !isExcluded(selected, rules)
            ? clean(selected.textContent)
            : undefined
    }
    if (
        element instanceof HTMLTextAreaElement ||
        (element instanceof HTMLInputElement &&
            !['checkbox', 'radio', 'submit', 'button', 'reset'].includes(
                element.type,
            ))
    ) {
        return clean(element.value, 200)
    }
    // The choice of a list box the page built itself: the user sees it on
    // the closed box, the agent would have to open it (seen on the bench: an
    // invitation sent with the role the agent never saw)
    if (roleOf(element) === 'combobox') {
        return comboboxChoice(element, rules, 200)
    }
    // A text box the page built itself is one line with no content below
    // it: its text is the value, whole, since the agent rewrites what it
    // reads (a prompt it adds to)
    if (roleOf(element) === 'textbox') {
        return editorText(element, rules, WHOLE_MAX_VALUE)
    }
    return undefined
}

/**
 * Languages a name may stand for besides the page's and the browser's: the
 * ones a site's language switch usually offers.
 */
const COMMON_LANGUAGES = [
    // Norwegian as `nb`: `no` is also the button that says no
    'it',
    'en',
    'de',
    'fr',
    'es',
    'pt',
    'nl',
    'pl',
    'sv',
    'da',
    'fi',
    'nb',
    'cs',
    'sk',
    'sl',
    'hr',
    'hu',
    'ro',
    'bg',
    'el',
    'lt',
    'lv',
    'et',
    'ru',
    'uk',
    'tr',
    'ar',
    'he',
    'zh',
    'ja',
    'ko',
]

const baseLanguage = (tag: string | null | undefined): string =>
    (tag ?? '').trim().toLowerCase().split(/[-_]/)[0] ?? ''

const normalizeName = (text: string): string =>
    text
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
        .trim()
        .toLowerCase()

let languageNames: Map<string, string> | undefined

/**
 * The language a name stands for: a code (`it`, `en-US`) or the language's
 * name in the page's language, in English or in itself ("Italiano",
 * "English", "Deutsch"). `Intl.DisplayNames` only goes from code to name, so
 * the names of a fixed set of candidates are listed once; a name two
 * languages share stands for none.
 */
export function languageNamed(name: string): string | undefined {
    if (!languageNames) {
        languageNames = new Map()
        const page = baseLanguage(document.documentElement.lang)
        const candidates = new Set(
            [
                page,
                ...(navigator.languages ?? []).map(baseLanguage),
                ...COMMON_LANGUAGES,
            ].filter(Boolean),
        )
        const add = (key: string, code: string) => {
            const known = languageNames?.get(key)
            languageNames?.set(
                key,
                known === undefined || known === code ? code : '',
            )
        }
        for (const code of candidates) {
            add(code, code)
            for (const shownIn of new Set([page || 'en', 'en', code])) {
                try {
                    const shown = new Intl.DisplayNames([shownIn], {
                        type: 'language',
                    }).of(code)
                    if (shown && shown !== code) {
                        add(normalizeName(shown), code)
                    }
                } catch {
                    // A code this browser cannot name
                }
            }
        }
    }
    const key = normalizeName(name)
    const code =
        languageNames.get(key) ??
        (/^[a-z]{2,3}([-_][a-z0-9]{2,8})+$/.test(key)
            ? languageNames.get(baseLanguage(key))
            : undefined)
    return code || undefined
}

/**
 * The language a field takes, where the page says it reliably (spec
 * `page-tools-improvements.md`, M5): a `lang` attribute on the field or on a
 * container of it below the document's root, or the tab of the panel the
 * field is in, when that tab names a language. The language the whole page
 * declares on `<html>` says nothing about one field.
 */
const fieldLanguage = (element: Element): string | undefined => {
    for (const ancestor of composedAncestors(element)) {
        if (ancestor === document.documentElement) {
            break
        }
        const lang = ancestor.getAttribute('lang')
        if (lang !== null) {
            return baseLanguage(lang) || undefined
        }
        if (ancestor.getAttribute('role') === 'tabpanel') {
            const root = ancestor.getRootNode() as Document | ShadowRoot
            const tab =
                (ancestor.getAttribute('aria-labelledby') ?? '')
                    .split(' ')
                    .map((id) => (id ? root.getElementById(id) : null))
                    .find(Boolean) ??
                (ancestor.id
                    ? root.querySelector(
                          `[role=tab][aria-controls~="${CSS.escape(ancestor.id)}"]`,
                      )
                    : null)
            const named = tab?.textContent
                ? languageNamed(tab.textContent)
                : undefined
            if (named) {
                return named
            }
        }
    }
    return undefined
}

/** Roles that take text: the fields whose language the snapshot tells. */
const TEXT_ROLES = new Set(['textbox', 'searchbox', 'combobox'])
/** Roles of the switches whose name may be a language: a tab, a menu item. */
const SWITCH_ROLES = new Set([
    'tab',
    'menuitem',
    'menuitemradio',
    'option',
    'button',
    'link',
    'radio',
])

/** One line for an element with a role: `[12] button "Invia" disabled`. */
export function describe(
    element: Element,
    ref: number,
    rules: PageRules,
): string {
    const role = roleOf(element) ?? 'generic'
    const level = /^H([1-6])$/.exec(element.tagName)?.[1]
    const parts = [`[${ref}] ${role}${level ? `(${level})` : ''}`]
    const name = nameOf(element, rules)
    if (name) {
        parts.push(JSON.stringify(name))
    }
    const value = valueOf(element, rules)
    if (value !== undefined) {
        parts.push(`value=${JSON.stringify(value)}`)
    }
    if (element instanceof HTMLSelectElement) {
        const options = allowedOptions(element, rules).map((o) =>
            clean(o.textContent, 40),
        )
        parts.push(`options=${JSON.stringify(options.slice(0, 20))}`)
    }
    if (element instanceof HTMLAnchorElement && element.href) {
        parts.push(`→ ${element.getAttribute('href')}`)
    }
    // The language of a text, and of the switch that shows another one: the
    // agent writes each text in the version of its own language
    const lang = TEXT_ROLES.has(role)
        ? fieldLanguage(element)
        : SWITCH_ROLES.has(role) && name
          ? languageNamed(name)
          : undefined
    if (lang) {
        parts.push(`lang ${lang}`)
    }
    parts.push(...stateOf(element))
    return parts.join(' ')
}

/**
 * A field of a form as the user sees it filled in. `before` is there when the
 * value changed since the agent first read it: what it was, empty when the
 * field had none.
 */
export type FieldSummary = { label: string; value: string; before?: string }

const SUMMARY_MAX_VALUE = 80
/**
 * A value as the form's fingerprint reads it: whole, but not without end. A
 * prompt runs to thousands of characters, and a change at its end counts.
 */
const WHOLE_MAX_VALUE = 20_000
/** Characters a changed long value keeps before the first one that differs. */
const CHANGE_CONTEXT = 20

/**
 * A long value as the card shows it once changed: from shortly before the
 * first character that differs, at the start of a word, so the user reads
 * what the agent wrote and not the opening it left as it was.
 */
const changedPart = (now: string, before: string): string => {
    let at = 0
    while (at < now.length && now[at] === before[at]) {
        at++
    }
    // At the start of a word, unless the word is long (an address): then
    // right before the change, or the excerpt would hold none of it
    const space = now.lastIndexOf(' ', at - CHANGE_CONTEXT)
    const from =
        at <= CHANGE_CONTEXT
            ? 0
            : space >= at - 2 * CHANGE_CONTEXT
              ? space + 1
              : at - CHANGE_CONTEXT
    return from > 0
        ? `…${clean(now.slice(from), SUMMARY_MAX_VALUE - 1)}`
        : clean(now, SUMMARY_MAX_VALUE)
}

/** A language by its name in the page's own language: "Italian" on an English page. */
const languageName = (code: string): string => {
    try {
        return (
            new Intl.DisplayNames(
                [document.documentElement.lang || navigator.language],
                { type: 'language' },
            ).of(code) ?? code
        )
    } catch {
        return code
    }
}

/** The group a radio button answers: its fieldset's legend or its radio group's name. */
const groupName = (radio: HTMLInputElement, rules: PageRules): string => {
    const group = radio.closest('[role=radiogroup], fieldset')
    if (group instanceof HTMLFieldSetElement) {
        const legend = group.querySelector(':scope > legend')
        if (legend) {
            return filteredText(legend, rules, 120)
        }
    }
    return (group ? nameOf(group, rules) : '') || radio.name
}

/** True for an element laid out on a line of its own: a block, or an item of a flex or grid. */
const startsLine = (element: Element): boolean =>
    /^(block|flex|grid|list-item|table)$/.test(
        getComputedStyle(element).display,
    ) ||
    (!!element.parentElement &&
        /(^|-)(flex|grid)$/.test(
            getComputedStyle(element.parentElement).display,
        ))

/**
 * An element's name as the user reads it, on a card or a step of the chat:
 * a field by the title of its label, without the description a label may
 * hold under it (a switch whose label reads "Allow uploads" over "Users
 * attach files to messages"). The title is the line of the label's first
 * text, when the label lays out more than one. Anything else by its name:
 * the model reads the whole name, in the snapshot.
 */
export function fieldTitle(element: Element, rules: PageRules): string {
    const name = nameOf(element, rules)
    const label = (element as HTMLInputElement).labels?.[0]
    if (!label || declaredName(element, rules)) {
        return name
    }
    const walker = document.createTreeWalker(label, NodeFilter.SHOW_TEXT)
    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
        const parent = node.parentElement
        if (
            !node.textContent?.trim() ||
            !parent ||
            element.contains(node) ||
            isExcluded(parent, rules)
        ) {
            continue
        }
        let line: Element | null = parent
        while (line && line !== label && !startsLine(line)) {
            line = line.parentElement
        }
        return (
            (line && line !== label && filteredText(line, rules, 120)) || name
        )
    }
    return name
}

const summaryOf = (
    element: Element,
    rules: PageRules,
    checked: string,
    whole: boolean,
): FieldSummary | undefined => {
    const max = whole ? WHOLE_MAX_VALUE : SUMMARY_MAX_VALUE
    const short = (text: string) => clean(text, max)
    // The fingerprint compares, nobody reads it: the name is enough there
    const label = () =>
        whole
            ? nameOf(element, rules) || roleOf(element) || ''
            : fieldTitle(element, rules) || roleOf(element) || ''
    if (element instanceof HTMLSelectElement) {
        const value = allowedOptions(element, rules)
            .filter((option) => option.selected)
            .map((option) => clean(option.textContent, 40))
            .join(', ')
        return value ? { label: label(), value: short(value) } : undefined
    }
    if (element instanceof HTMLInputElement) {
        switch (element.type) {
            case 'checkbox':
                return element.checked
                    ? { label: label(), value: checked }
                    : undefined
            case 'radio':
                return element.checked
                    ? {
                          label: groupName(element, rules),
                          value: short(nameOf(element, rules)),
                      }
                    : undefined
            case 'file':
                return element.files?.length
                    ? {
                          label: label(),
                          value: short(
                              [...element.files]
                                  .map((file) => file.name)
                                  .join(', '),
                          ),
                      }
                    : undefined
            case 'submit':
            case 'button':
            case 'reset':
            case 'image':
                return undefined
        }
    }
    if (
        element instanceof HTMLInputElement ||
        element instanceof HTMLTextAreaElement
    ) {
        return element.value.trim()
            ? { label: label(), value: short(element.value) }
            : undefined
    }
    if (roleOf(element) === 'combobox') {
        const choice = comboboxChoice(element, rules, max, { byText: whole })
        return choice ? { label: label(), value: short(choice) } : undefined
    }
    // A rich editor: the text it shows, not the hint it shows when empty
    const value = editorText(element, rules, max)
    return value ? { label: label(), value } : undefined
}

/**
 * The fields of a form the user fills in, in reading order: its controls, and
 * the editors and list boxes a site built itself. Leaves out the excluded,
 * hidden and disabled ones (disabled by themselves or by their fieldset: they
 * send nothing), and an editor nested in another, already counted.
 */
const formFields = (form: HTMLFormElement, rules: PageRules): Element[] =>
    [
        ...new Set<Element>([
            // The controls only: a fieldset or an output is in `elements` too
            ...[...form.elements].filter(
                (element) =>
                    element instanceof HTMLInputElement ||
                    element instanceof HTMLSelectElement ||
                    element instanceof HTMLTextAreaElement,
            ),
            // Editors and list boxes that are not form controls: what a site
            // built with its own components fills in
            ...form.querySelectorAll(
                '[contenteditable=""], [contenteditable="true"], [role=textbox]:not(input, textarea), [role=combobox]:not(input, select)',
            ),
        ]),
    ].filter(
        (element) =>
            !element.matches(':disabled') &&
            !isExcluded(element, rules) &&
            !isHidden(element, { inert: false }) &&
            !(
                (element as HTMLElement).isContentEditable &&
                (element.parentElement as HTMLElement | null)?.isContentEditable
            ),
    )

/**
 * Each field of a form with its whole value, an empty one included: what a
 * submit card compares with, to list first what changed. The fields `known`
 * already holds are left out, so a page read again costs only its new ones.
 */
export function formValues(
    form: HTMLFormElement,
    rules: PageRules,
    checked: string,
    known?: Pick<WeakMap<Element, string>, 'has'>,
): Map<Element, string> {
    return new Map(
        formFields(form, rules)
            .filter((element) => !known?.has(element))
            .map((element) => [
                element,
                summaryOf(element, rules, checked, true)?.value ?? '',
            ]),
    )
}

/**
 * The fields of a form as its user sees them filled in, for the card that
 * asks before submitting it (spec `page-tools-improvements.md`, M2): what
 * the user judges, not the exact body of the request, which the site's own
 * handlers may change. Leaves out the excluded and sensitive fields (with
 * their labels, as the snapshot does), the hidden, disabled and empty ones,
 * and the buttons. `checked` and `unchecked` are the values of a checkbox.
 *
 * `fields` are the changes, each with what it was, in the order of the
 * form; `unchanged` the other fields that send a value, which the card
 * folds: on a page of settings they are most of the form.
 */
export function formSummary(
    form: HTMLFormElement,
    rules: PageRules,
    {
        checked,
        unchecked = '',
        whole = false,
        before,
    }: {
        checked: string
        unchecked?: string
        /**
         * Every field with its whole value, a list box by the text it shows:
         * what tells whether the form changed while its card waited, the
         * folded fields and the ends of long values included.
         */
        whole?: boolean
        /**
         * The whole value each field had before (`formValues`): a field
         * whose value differs is a change, a long one shown from where it
         * changed. A field with no value before counts as unchanged.
         */
        before?: Pick<WeakMap<Element, string>, 'get'>
    },
): { fields: FieldSummary[]; unchanged: FieldSummary[] } {
    const elements = formFields(form, rules)
    if (whole) {
        return {
            fields: elements.flatMap((element) => {
                const field = summaryOf(element, rules, checked, true)
                return field ? [field] : []
            }),
            unchanged: [],
        }
    }
    // The version a text belongs to, which the label alone does not say
    // when a page has one per language
    const titled = (element: Element, label: string) => {
        const lang = TEXT_ROLES.has(roleOf(element) ?? '')
            ? fieldLanguage(element)
            : undefined
        return lang ? `${label} (${languageName(lang)})` : label
    }
    const isCheckbox = (element: Element) =>
        element instanceof HTMLInputElement && element.type === 'checkbox'
    // A value as the card shows it, an unticked box by its word
    const shown = (element: Element, value: string) => {
        if (!value) {
            return isCheckbox(element) ? unchecked : ''
        }
        return clean(value, SUMMARY_MAX_VALUE)
    }
    const changes: FieldSummary[] = []
    const unchanged: FieldSummary[] = []
    // A radio group is one change: the button it left gives the "before"
    // of the one it took, whichever comes first in the form
    const leftChoices = new Map<string, { label: string; was: string }>()
    const takenChoices = new Map<string, FieldSummary>()
    for (const element of elements) {
        const field = summaryOf(element, rules, checked, false)
        const was = before?.get(element)
        const now = summaryOf(element, rules, checked, true)?.value ?? ''
        if (was === undefined || was === now) {
            if (field) {
                unchanged.push({
                    label: titled(element, field.label),
                    value: field.value,
                })
            }
            continue
        }
        if (element instanceof HTMLInputElement && element.type === 'radio') {
            const group = element.name || groupName(element, rules)
            const label = groupName(element, rules)
            if (!now) {
                leftChoices.set(group, { label, was })
                continue
            }
            const change = { label, before: '', value: shown(element, now) }
            takenChoices.set(group, change)
            changes.push(change)
            continue
        }
        // Past the length the card shows, both sides from where they part:
        // the old text ends where the new one goes on
        const long = Math.max(was.length, now.length) > SUMMARY_MAX_VALUE
        changes.push({
            label: titled(
                element,
                field?.label ??
                    (fieldTitle(element, rules) || roleOf(element) || ''),
            ),
            before: long && was ? changedPart(was, now) : shown(element, was),
            value:
                long && now
                    ? changedPart(now, was)
                    : (field?.value ?? shown(element, now)),
        })
    }
    for (const [group, { label, was }] of leftChoices) {
        const taken = takenChoices.get(group)
        if (taken) {
            taken.before = clean(was, SUMMARY_MAX_VALUE)
        } else {
            changes.push({
                label,
                before: clean(was, SUMMARY_MAX_VALUE),
                value: '',
            })
        }
    }
    return { fields: changes, unchanged }
}

export type SnapshotResult = { text: string; truncated: boolean }

/**
 * Walks `root` in reading order, through open shadow roots and slots. An
 * element with a role becomes one line and is not descended (its text is its
 * name), but for a container, whose content follows its line, and a role of
 * structure, read as text; any other element contributes its own text nodes.
 * Stops at `maxChars`, or past the node and time budget.
 */
export function snapshot(
    root: Element,
    refs: PageRefs,
    rules: PageRules,
    { maxChars, withRefs = true }: { maxChars: number; withRefs?: boolean },
): SnapshotResult {
    const lines: string[] = []
    const started = performance.now()
    let length = 0
    let nodes = 0
    let truncated = false

    const push = (line: string) => {
        if (truncated || !line) {
            return
        }
        if (length + line.length + 1 > maxChars) {
            truncated = true
            return
        }
        lines.push(line)
        length += line.length + 1
    }

    const visit = (node: Node) => {
        if (truncated) {
            return
        }
        if (++nodes > MAX_NODES || performance.now() - started > MAX_WALK_MS) {
            truncated = true
            return
        }
        if (node.nodeType === Node.TEXT_NODE) {
            push(clean(node.textContent, 1000))
            return
        }
        if (!(node instanceof Element)) {
            return
        }
        if (SKIPPED_TAGS.has(node.tagName) || excludesItself(node, rules)) {
            return
        }
        if (isHidden(node)) {
            return
        }
        const role = roleOf(node)
        if (
            role &&
            withRefs &&
            (!CONTENT_ROLES.has(role) || choosableCell(node, role))
        ) {
            push(describe(node, refs.refOf(node), rules))
            if (!CONTAINER_ROLES.has(role)) {
                return
            }
        }
        for (const child of childrenOf(node)) {
            visit(child)
        }
    }

    if (!isExcluded(root, rules)) {
        visit(root)
    }
    return { text: lines.join('\n'), truncated }
}
